import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import { shareContent } from '../../utils/sharingUtils';

const VendorActionBar = ({ 
  vendorId,
  vendorName = "Vendor",
  isAvailable = true,
  isFavorited = false,
  onMessage,
  onCheckAvailability,
  onBookNow,
  onToggleFavorite,
  onShare,
  contactInfo = {}
}) => {
  const [isSticky, setIsSticky] = useState(false);
  const [showContactOptions, setShowContactOptions] = useState(false);
  const [shareStatus, setShareStatus] = useState(null);

  React.useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsSticky(scrollPosition > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleMessage = () => {
    onMessage?.(vendorId);
  };

  const handleCheckAvailability = () => {
    onCheckAvailability?.(vendorId);
  };

  const handleBookNow = () => {
    onBookNow?.(vendorId);
  };

  const handleToggleFavorite = () => {
    onToggleFavorite?.(vendorId, !isFavorited);
  };

  const handleShare = async () => {
    setShareStatus(null);
    
    try {
      const result = await shareContent({
        title: `${vendorName} - ShaadiNest`,
        text: `Check out ${vendorName} on ShaadiNest`,
        url: window.location.href,
        fallbackMessage: 'Vendor profile link copied to clipboard!'
      }, {
        onSuccess: ({ method, message }) => {
          setShareStatus({ type: 'success', message: message || 'Shared successfully!' });
          // Clear status after 3 seconds
          setTimeout(() => setShareStatus(null), 3000);
        },
        onError: ({ error }) => {
          setShareStatus({ type: 'error', message: 'Unable to share. Please try again.' });
          setTimeout(() => setShareStatus(null), 3000);
        }
      });

      // Call the original onShare callback if provided
      onShare?.(vendorId, result);
    } catch (error) {
      console.error('Share error:', error);
      setShareStatus({ type: 'error', message: 'Unable to share. Please try again.' });
      setTimeout(() => setShareStatus(null), 3000);
    }
  };

  const handleCall = () => {
    if (contactInfo.phone) {
      window.location.href = `tel:${contactInfo.phone}`;
    }
  };

  const handleEmail = () => {
    if (contactInfo.email) {
      window.location.href = `mailto:${contactInfo.email}`;
    }
  };

  return (
    <>
      {/* Share Status Toast */}
      {shareStatus && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-2 rounded-lg shadow-strong animate-slide-down ${
          shareStatus.type === 'success' ?'bg-success text-white' :'bg-error text-white'
        }`}>
          <div className="flex items-center space-x-2">
            <Icon 
              name={shareStatus.type === 'success' ? 'CheckCircle' : 'AlertCircle'} 
              size={16} 
              color="currentColor" 
            />
            <span className="text-sm font-medium">{shareStatus.message}</span>
          </div>
        </div>
      )}

      {/* Desktop Action Bar */}
      <div className={`hidden lg:block transition-all duration-200 ${
        isSticky 
          ? 'fixed top-20 left-0 right-0 z-40 bg-white/95 backdrop-blur-sm shadow-medium border-b border-border' 
          : 'relative bg-white'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`flex items-center justify-between ${isSticky ? 'py-3' : 'py-4'}`}>
            {/* Vendor Info (when sticky) */}
            {isSticky && (
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center">
                  <Icon name="Store" size={20} color="#8B4513" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-text-primary">{vendorName}</h3>
                  <div className="flex items-center space-x-2">
                    <div className={`w-2 h-2 rounded-full ${isAvailable ? 'bg-success' : 'bg-warning'}`} />
                    <span className="text-sm text-text-secondary font-caption">
                      {isAvailable ? 'Available' : 'Busy'}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center space-x-3">
              {/* Secondary Actions */}
              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  iconName={isFavorited ? "Heart" : "Heart"}
                  iconSize={18}
                  onClick={handleToggleFavorite}
                  className={isFavorited ? 'text-accent hover:text-accent-600' : 'text-text-secondary hover:text-accent'}
                >
                  {isFavorited ? 'Saved' : 'Save'}
                </Button>
                
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Share"
                  iconSize={18}
                  onClick={handleShare}
                  className="text-text-secondary hover:text-primary"
                >
                  Share
                </Button>

                <div className="relative">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Phone"
                    iconSize={18}
                    onClick={() => setShowContactOptions(!showContactOptions)}
                    className="text-text-secondary hover:text-primary"
                  >
                    Contact
                  </Button>
                  
                  {showContactOptions && (
                    <div className="absolute top-full right-0 mt-1 bg-white rounded-lg shadow-strong border border-border-light z-50 min-w-48 animate-slide-down">
                      <div className="p-2">
                        {contactInfo.phone && (
                          <button
                            onClick={handleCall}
                            className="w-full flex items-center space-x-3 px-3 py-2 rounded-md hover:bg-surface-100 transition-smooth text-left"
                          >
                            <Icon name="Phone" size={16} color="#6B5B4F" />
                            <div>
                              <div className="text-sm font-medium text-text-primary">Call</div>
                              <div className="text-xs text-text-secondary font-data">{contactInfo.phone}</div>
                            </div>
                          </button>
                        )}
                        {contactInfo.email && (
                          <button
                            onClick={handleEmail}
                            className="w-full flex items-center space-x-3 px-3 py-2 rounded-md hover:bg-surface-100 transition-smooth text-left"
                          >
                            <Icon name="Mail" size={16} color="#6B5B4F" />
                            <div>
                              <div className="text-sm font-medium text-text-primary">Email</div>
                              <div className="text-xs text-text-secondary font-caption">{contactInfo.email}</div>
                            </div>
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Primary Actions */}
              <div className="flex items-center space-x-3 pl-3 border-l border-border">
                <Button
                  variant="outline"
                  size="md"
                  iconName="MessageCircle"
                  iconSize={18}
                  onClick={handleMessage}
                >
                  Message
                </Button>
                
                <Button
                  variant="secondary"
                  size="md"
                  iconName="Calendar"
                  iconSize={18}
                  onClick={handleCheckAvailability}
                >
                  Check Availability
                </Button>
                
                <Button
                  variant="primary"
                  size="md"
                  iconName="BookOpen"
                  iconSize={18}
                  onClick={handleBookNow}
                  disabled={!isAvailable}
                >
                  Book Now
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Action Bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-border shadow-strong">
        <div className="px-4 py-3">
          <div className="flex items-center space-x-3">
            {/* Mobile Secondary Actions */}
            <div className="flex items-center space-x-2">
              <button
                onClick={handleToggleFavorite}
                className={`flex items-center justify-center w-10 h-10 rounded-lg transition-smooth ${
                  isFavorited 
                    ? 'bg-accent-50 text-accent hover:bg-accent-100' :'bg-surface-100 text-text-secondary hover:bg-surface-200 hover:text-accent'
                }`}
              >
                <Icon name="Heart" size={20} color="currentColor" />
              </button>
              
              <button
                onClick={handleShare}
                className="flex items-center justify-center w-10 h-10 rounded-lg bg-surface-100 text-text-secondary hover:bg-surface-200 hover:text-primary transition-smooth"
              >
                <Icon name="Share" size={20} color="currentColor" />
              </button>
            </div>

            {/* Mobile Primary Actions */}
            <div className="flex-1 flex items-center space-x-2">
              <Button
                variant="outline"
                size="md"
                iconName="MessageCircle"
                iconSize={18}
                onClick={handleMessage}
                fullWidth
              >
                Message
              </Button>
              
              <Button
                variant="primary"
                size="md"
                iconName="BookOpen"
                iconSize={18}
                onClick={handleBookNow}
                disabled={!isAvailable}
                fullWidth
              >
                Book Now
              </Button>
            </div>
          </div>

          {/* Mobile Availability Status */}
          <div className="flex items-center justify-center space-x-2 mt-2 pt-2 border-t border-border-light">
            <div className={`w-2 h-2 rounded-full ${isAvailable ? 'bg-success' : 'bg-warning'}`} />
            <span className="text-sm text-text-secondary font-caption">
              {isAvailable ? 'Available for booking' : 'Currently busy - check availability'}
            </span>
          </div>
        </div>
      </div>

      {/* Mobile Spacer */}
      <div className="lg:hidden h-24" />
    </>
  );
};

export default VendorActionBar;